import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "components/modals/ModalContext";
import { EventType } from "../SchedulingTypes";
import { determineStatusColorClass } from "../functions/determineStatusColorClass";
import { calculateEventLanes } from "../functions/calculateOverlappingEvents";
import JobModal from "../../../views/jobs/JobModal";
import EditActivityForm from "../EditActivityForm";
import { Draggable } from "services/dragdrop/Draggable";
import { truncate } from "services/utils/truncate";
import { AppointmentTextOption, APPOINTMENT_TEXT_SEPARATOR, formatAppointmentText } from "../functions/appointmentText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

interface CalendarEventProps {
    event: EventType;
    appointmentTextOptions: AppointmentTextOption[];
    colorMethod: string | null;
    currentResource: string | number;
    currentDay: string;
    pixelsPerMinute?: number;
    dayStartTime: string;
    overlappingEvents?: [EventType[], number];
    onOpenRightClickMenu: (menu: any) => void;
    onCloseRightClickMenu?: () => void;
    onShowTooltip: (x: number, y: number, event: EventType) => void;
    onHideTooltip: () => void;
    onRefresh: () => void;
}

const CalendarEvent: React.FC<CalendarEventProps> = ({
    event,
    appointmentTextOptions,
    colorMethod,
    currentResource,
    currentDay,
    pixelsPerMinute,
    dayStartTime,
    overlappingEvents,
    onOpenRightClickMenu,
    onCloseRightClickMenu,
    onShowTooltip,
    onHideTooltip,
    onRefresh
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const eventEntered = useRef(false);
    const eventPosition = useRef<{ x: number; y: number } | null>(null);

    // Get the timeslot from the event
    const getEventTimeSlot = (startDateTime?: string) => {
        if (!startDateTime) return undefined;

        const time = startDateTime.split("T")[1]; // "09:30:00"
        const [hours, minutes] = time.split(":").map(Number);

        // snap naar jouw slot (bijv. 30 min blocks)
        const roundedMinutes = minutes < 30 ? "00" : "30";

        return `${String(hours).padStart(2, "0")}:${roundedMinutes}`;
    };

    // Get the timeslot from the event
    const timeSlot = getEventTimeSlot(event.startDateTime);

    // Get the minutes from the day start
    const getMinutesFromDayStart = () => {
        if (!event.startDateTime) return 0;

        const [eventHours, eventMinutes] = event.startDateTime.split("T")[1].split(":").map(Number);
        const [startHours, startMinutes] = dayStartTime.split(":").map(Number);

        const eventTotal = eventHours * 60 + eventMinutes;
        const startTotal = startHours * 60 + startMinutes;

        return eventTotal - startTotal;
    };

    // Get the starttime offset
    const getStartTimeOffset = () => {
        if (!pixelsPerMinute) return 0;
        return getMinutesFromDayStart() * pixelsPerMinute;
    };

    // Get the duration height
    const getDurationHeight = () => {
        if (!pixelsPerMinute || !event.durationMinutes) return 0;
        return (event.durationMinutes * pixelsPerMinute) - 1;
    };

    // Get the event position style
    const getEventPositionStyle = (): React.CSSProperties => {
        const [overlapList, widthPercentage] = overlappingEvents ?? [[], 100];

        // Determine the stack lane of the event, events without visual
        // overlap share the same lane so they stay next to each other
        const eventLanes = calculateEventLanes(overlapList);
        const lane = eventLanes.get(event.key) ?? 0;

        return {
            position: "absolute",
            top: `${getStartTimeOffset()}px`,
            left: `calc(${lane * widthPercentage}% + 1px)`,
            width: `calc(${widthPercentage}% - 2px)`,
            height: `${getDurationHeight()}px`,
        };
    };

    // Get event class name
    const getEventClassName = () => {
        let className = "event";

        if (event.type === "appointment") {
            if (colorMethod === "custom") {
                className += event.custom_color
                    ? ` ${event.custom_color}`
                    : " default-event-color";
            } else if (colorMethod === "type") {
                className += event.job?.type_color
                    ? ` ${event.job.type_color}`
                    : " default-event-color";
            } else if (event.job) {
                className += ` ${determineStatusColorClass(event)}`;
            }
        }

        if (event.type === "timerow") {
            if (colorMethod === "type") {
                className += event.job?.type_color
                    ? ` ${event.job.type_color}`
                    : " default-event-color";
            } else {
                className += ` ${determineStatusColorClass(event)}`;
            }
        }

        // Mark activity events so their text color can differ from appointments
        if (event.type === "activity") {
            className += event.custom_color
                ? ` activity ${event.custom_color}`
                : " activity default-activity-event-color";
        }

        return className;
    };

    // Render event content
    const renderEventContent = () => {
        switch (event.type) {
            case "appointment":
            case "timerow": {
                // Build the text from the chosen job fields of the appointment text setting
                const appointmentText = formatAppointmentText(event.job, appointmentTextOptions);

                return (
                    <div className="event-content">
                        {event.type === "appointment" && event.locked && (
                            <span className='event-locked-icon'>
                                <FontAwesomeIcon icon={faLock} />
                            </span>
                        )}
                        <span className='event-start-time'>{event.startTime}</span>

                        {appointmentText && (
                            <span className='event-text'>{APPOINTMENT_TEXT_SEPARATOR}{appointmentText}</span>
                        )}
                    </div>
                );
            }

            case "activity":
                return (
                    <div className="event-content">
                        <span>{event.startTime}</span>
                        {event.time_type_name && (
                            <span> • {event.time_type_name}</span>
                        )}
                        {event.note && <span> - {truncate(event.note, 40)}</span>}
                    </div>
                );

            default:
                return null;
        }
    };

    // Handle event click
    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Close the right click menu, if it is opened
        onCloseRightClickMenu?.();

        if (event.type === "appointment" || event.type === "timerow") {
            initializeModal(
                React.cloneElement(<JobModal />, { itemId: event.job.id }), { 
                    itemId: event.job.id, 
                    modalSize: "large",
                    onClose: () => {
                        // Trigger scheduler event refresh in case the job is changed
                        onRefresh();
                    } 
                }
            );
        }

        if (event.type === "activity") {
            initializeModal(
                React.cloneElement(<EditActivityForm />, { itemId: event.activityId }), {
                    title: t("scheduling.other_activity.edit_activity_label"),
                    itemId: event.activityId,
                    modalSize: "small",
                    onSuccess: () => {
                        // Trigger scheduler event refresh
                        onRefresh();
                    }
                }
            );
        }
    };

    // Show tooltip on mouse enter
    const handleMouseEnter = (e: React.MouseEvent) => {
        eventEntered.current = true;
        eventPosition.current = { x: e.clientX, y: e.clientY };
        onShowTooltip(e.clientX + 12, e.clientY + 12, event);
    };

    // Show tooltip on mouse mouve
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!eventEntered.current && !eventPosition.current) {
            onShowTooltip(e.clientX + 12, e.clientY + 12, event);
        }
    };

    // Hide tooltip on mouse leave
    const handleMouseLeave = () => {
        eventPosition.current = null;
        onHideTooltip();
    };

    // Handle open right click menu
    const handleOpenRightClickMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Block right click menu for timerows
        if (event.type === 'timerow') return;

        onOpenRightClickMenu({
            type: 'event',
            x: e.clientX,
            y: e.clientY,
            event
        });
    };

    return (
         <Draggable
            key={event.key}
            draggableId={ 
                event.type === 'appointment' 
                    ? { type: 'appointment', event } 
                    : event.type === 'activity' 
                        ? { type: 'activity', event }
                        : null 
            }
            isDragDisabled={ 
                event.type === "timerow"
                    ? {
                        disabled: true,
                        message: "scheduling.scheduling_board.timerow_event_dragging_disabled",
                    }
                    : event.type === "appointment" && event.status === "done"
                    ? {
                        disabled: true,
                        message: "scheduling.scheduling_board.done_appointment_dragging_disabled",
                    }
                    : event.type === "appointment" && event.locked
                    ? {
                        disabled: true,
                        message: t('scheduling.scheduling_board.locked_appointment_dragging_disabled'),
                    }
                    : undefined
            }
            sourceDroppableArea={{ 
                type: 'timeSlot',
                resourceId: currentResource,
                day: currentDay,
                ...(timeSlot ? { timeSlot } : {})
            }}
        >
            <div
                className={getEventClassName()}
                style={getEventPositionStyle()}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onContextMenu={handleOpenRightClickMenu}
            >
                {renderEventContent()}
            </div>
        </Draggable>
    );
};

export default CalendarEvent;